import React from 'react';
import { format } from 'date-fns';
import { Check, X, Eye } from 'lucide-react';
import { BlogPost } from '../../types/blog';
import { Link } from 'react-router-dom';

interface BlogApprovalQueueProps {
  posts: BlogPost[];
  onApprove: (postId: string) => void;
  onReject: (postId: string) => void;
}

export const BlogApprovalQueue: React.FC<BlogApprovalQueueProps> = ({
  posts,
  onApprove,
  onReject,
}) => {
  const pendingPosts = posts.filter((post) => post.status === 'pending_review');

  if (pendingPosts.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg">
        <p className="text-gray-500">No posts pending review.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium text-gray-900">Posts Pending Review</h3>
      </div>
      <ul className="divide-y divide-gray-200">
        {pendingPosts.map((post) => (
          <li key={post.id} className="px-4 py-5 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="text-lg font-medium text-gray-900 truncate">
                  {post.title}
                </h4>
                <div className="mt-1">
                  <p className="text-sm text-gray-500">
                    By {post.author.name} • Submitted{' '}
                    {format(new Date(post.createdAt), 'MMM d, yyyy')}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4 ml-4">
                <Link
                  to={`/blog/preview/${post.id}`}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Link>
                <button
                  onClick={() => onApprove(post.id)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </button>
                <button
                  onClick={() => onReject(post.id)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};